/**
 * Sitzungslogik der Sammel-Entscheidungen (Konvergenz-5).
 *
 * Eigenstaendiges Modul aus demselben Grund wie `emptyStateGates.ts`:
 * `useWorkbench.ts` zieht Supabase + i18n mit, reine Mengenlogik bleibt
 * abhaengigkeitsfrei und damit ohne Hook-Gerüst testbar.
 *
 * Hintergrund: die RPC `bulk_update_approval_status` aktualisiert
 * serverseitig NUR Zeilen mit `approval_status = 'pending_review'` und gibt
 * lediglich die Anzahl zurueck, nicht die getroffenen IDs. Die lokale
 * Sitzungs-Map muss dieser Semantik folgen, nicht umgekehrt: eine
 * Sammel-Freigabe darf eine in der Sitzung getroffene Einzel-Ablehnung
 * nicht stillschweigend als Freigabe ueberschreiben. Vorher schrieb
 * `bulkApprove` alle uebergebenen IDs als 'approved' in die Map — die DB
 * behielt 'rejected', Sitzungszaehler und der "Alles ueberprueft"-
 * Leerzustand zaehlten damit Produkte als upload-bereit, die nie zu Google
 * gehen.
 */

/**
 * Nimmt aus einer Kandidatenliste alle IDs heraus, ueber die in dieser
 * Sitzung bereits entschieden wurde (Eintrag in der Entscheidungs-Map,
 * egal ob Freigabe oder Ablehnung).
 *
 * Damit zeigt der Sammelfreigabe-Knopf ("Alle ab 70 Punkten freigeben (N)")
 * denselben Umfang, den die RPC tatsaechlich noch treffen kann — auch
 * zwischen zwei Refetches der feed-weiten Kandidaten-Query: die Map ist
 * synchron, die Query 30 Sekunden gecacht.
 *
 * OHNE PRODUKTIV-AUFRUFER seit Etappe 4 (2026-08-22): der beschriebene Knopf
 * und die client-seitige Kandidatenliste dahinter sind entfallen, die Zahlen
 * kommen jetzt aus `workbench_bulk_summary` und die Auswahl trifft
 * `workbench_bulk_approve` serverseitig. Bewusst nicht geloescht, weil die
 * Funktion rein, getestet und fuer einen kuenftigen Auswahl-Pfad in der
 * Leiste unveraendert richtig ist. Wer sie wieder anschliesst, beachte: sie
 * kann nur IDs filtern, die der Client kennt - der Serverpfad kennt sie nicht.
 */
export function filterUndecided(
  ids: readonly string[],
  decisions: ReadonlyMap<string, unknown>,
): string[] {
  return ids.filter((id) => !decisions.has(id));
}

/**
 * Traegt eine Sammel-Entscheidung in die Sitzungs-Map ein, ohne bereits
 * getroffene Entscheidungen zu ueberschreiben — dieselbe Vorfahrtsregel,
 * die die RPC serverseitig via `approval_status = 'pending_review'` fuer
 * die Datenbank durchsetzt.
 *
 * Gibt eine neue Map zurueck (React-State-Vertrag), `prev` bleibt
 * unangetastet.
 */
export function applyBulkDecision<D>(
  prev: ReadonlyMap<string, D>,
  ids: readonly string[],
  decision: D,
): Map<string, D> {
  const next = new Map(prev);
  for (const id of ids) {
    if (!next.has(id)) next.set(id, decision);
  }
  return next;
}

/**
 * Nimmt eine Sammel-Entscheidung zurueck: entfernt die genannten IDs
 * VOLLSTAENDIG aus der Sitzungs-Map, statt sie auf einen dritten Wert zu
 * setzen.
 *
 * Gegenstueck zu `applyBulkDecision` fuer den Rueckweg der Sammelfreigabe
 * (Etappe 4, "Rueckgaengig" -> `workbench_bulk_reopen`, das serverseitig
 * 'approved' wieder auf 'pending_review' setzt).
 *
 * Loeschen und nicht "auf 'pending_review' setzen", und das ist der ganze
 * Punkt dieser Funktion: `countUndecidedLoaded` und der
 * "alles geprueft"-Leerzustand fragen nur die ANWESENHEIT eines Schluessels
 * ab, nicht seinen Wert - fuer sie ist jeder Eintrag eine getroffene
 * Entscheidung. Ein Eintrag mit dem Wert 'pending_review' liesse die Werkbank
 * also weiter "Alles ueberprueft" ueber genau den Zeilen melden, die gerade
 * wieder geoeffnet wurden.
 *
 * Unbekannte IDs sind folgenlos (`Map.delete` auf einem fehlenden Schluessel).
 * Das ist der Normalfall und kein Randfall: `workbench_bulk_approve` liefert
 * die IDs, die es geschrieben hat, und die muessen nicht in dieser Sitzung
 * einzeln entschieden worden sein.
 *
 * Gibt eine neue Map zurueck (React-State-Vertrag), `prev` bleibt
 * unangetastet.
 */
export function removeBulkDecisions<D>(
  prev: ReadonlyMap<string, D>,
  ids: readonly string[],
): Map<string, D> {
  const next = new Map(prev);
  for (const id of ids) {
    next.delete(id);
  }
  return next;
}
